import { forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import dayjs, { Dayjs } from 'dayjs';
import { Calendar, ChevronDown } from 'lucide-react';
import { cn } from './cn';
import { Dropdown } from './Dropdown';
import { MenuItem } from './Menu';

export interface DateRangePreset {
  label: string;
  value: [Dayjs, Dayjs];
}

export interface DateRangePickerProps {
  value: [Dayjs, Dayjs];
  onChange: (range: [Dayjs, Dayjs]) => void;
  presets?: DateRangePreset[];
  className?: string;
}

// A single-date trigger ("From 2026-05-21" / "To 2026-06-20"). react-datepicker
// clones this with `value` + `onClick`; the label is ours.
const DateTrigger = forwardRef<HTMLButtonElement, { value?: string; onClick?: () => void; label?: string }>(
  function DateTrigger({ value, onClick, label }, ref) {
    return (
      <button
        type="button"
        ref={ref}
        onClick={onClick}
        className={cn(
          'inline-flex h-9 items-center gap-1.5 rounded-control border border-line bg-surface px-2.5 text-sm',
          'transition-colors hover:border-brand-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
        )}
      >
        <Calendar size={14} className="text-muted" />
        {label && <span className="text-[11px] font-semibold uppercase tracking-wide text-subtle">{label}</span>}
        <span className="tabular-nums text-content">{value}</span>
      </button>
    );
  },
);

/**
 * Explicit From / To date pickers (replaces the ambiguous single range field).
 * Each is an independent single-date calendar, mutually constrained so From ≤ To.
 * Presets remain available via a compact "Quick" dropdown. Calendar visuals are
 * themed in styles/theme.css (`.react-datepicker*`).
 */
export function DateRangePicker({ value, onChange, presets = [], className }: DateRangePickerProps) {
  const [start, end] = value;

  return (
    <div className={cn('inline-flex flex-wrap items-center gap-2', className)}>
      <DatePicker
        selected={start.toDate()}
        onChange={(d: Date | null) => d && onChange([dayjs(d), end])}
        maxDate={(end.isAfter(dayjs(), 'day') ? dayjs() : end).toDate()}
        dateFormat="yyyy-MM-dd"
        customInput={<DateTrigger label="From" />}
        popperClassName="gb-datepicker-popper"
      />
      <DatePicker
        selected={end.toDate()}
        onChange={(d: Date | null) => d && onChange([start, dayjs(d)])}
        minDate={start.toDate()}
        maxDate={dayjs().toDate()}
        dateFormat="yyyy-MM-dd"
        customInput={<DateTrigger label="To" />}
        popperClassName="gb-datepicker-popper"
      />

      {presets.length > 0 && (
        <Dropdown
          trigger="click"
          panel={presets.map((p) => (
            <MenuItem key={p.label} onClick={() => onChange(p.value)}>
              {p.label}
            </MenuItem>
          ))}
        >
          <button
            type="button"
            className="inline-flex h-9 items-center gap-1 rounded-control border border-line bg-surface px-2.5 text-sm text-muted transition-colors hover:border-brand-400 hover:text-brand-700"
          >
            Quick
            <ChevronDown size={14} />
          </button>
        </Dropdown>
      )}
    </div>
  );
}
